import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Plus,
  ArrowRight,
  Activity,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [stats, setStats] = useState({ 
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    double_verified: 0,
    double_verified_pct: 0,
    added_this_month: 0,
    approved_this_month: 0,
    current_month: '',
    monthly_stats: [],
    recent_activity: [] 
  });
  const [loading, setLoading] = useState(true);
  const { canEdit, canApprove, user } = useAuth();

  // Derive a friendly display name from the user's email (e.g. "arpit.samdani@x.io" -> "Arpit Samdani")
  const displayName = (() => {
    const local = (user?.email || '').split('@')[0];
    if (!local) return user?.name || '';
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  })();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Factors', value: stats.total, icon: FileText, color: 'text-primary', bg: 'bg-primary/10',
      sub: stats.approved > 0 ? `${(stats.double_verified || 0).toLocaleString()} · ${stats.double_verified_pct}% cross-checked` : null },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
    { label: 'Approved', value: stats.approved, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100' },
    { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' },
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      create: 'Created',
      update: 'Updated',
      approve: 'Approved',
      reject: 'Rejected',
      delete: 'Deleted'
    };
    return labels[action] || action;
  };

  // Calculate max value for chart scaling
  const maxMonthlyValue = Math.max(
    ...stats.monthly_stats.map(m => Math.max(m.added, m.approved)),
    1
  );

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              {displayName ? `Welcome back, ${displayName}` : 'Dashboard'}
            </h1>
            <p className="text-muted-foreground mt-1">Overview of emission factor management</p>
          </div>
          {canEdit() && (
            <Link to="/emission-factors/new">
              <Button className="btn-primary gap-2" data-testid="new-ef-btn">
                <Plus className="w-4 h-4" />
                New Emission Factor
              </Button>
            </Link>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-gray-200 card-hover" data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="text-3xl font-heading font-bold mt-1">
                        {loading ? '—' : stat.value}
                      </p>
                      {!loading && stat.sub && (
                        <p className="text-xs text-muted-foreground/70 mt-1" data-testid="double-verified-note">
                          {stat.sub}
                        </p>
                      )}
                    </div>
                    <div className={`w-12 h-12 rounded-lg ${stat.bg} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Monthly Stats Chart */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Monthly Emission Factors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-64 bg-gray-100 rounded animate-pulse" />
            ) : stats.monthly_stats?.length > 0 ? (
              <div className="space-y-4">
                {/* Chart Legend */}
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-primary" />
                    <span className="text-muted-foreground">Added</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-500" />
                    <span className="text-muted-foreground">Approved</span>
                  </div>
                </div>
                
                {/* Bar Chart */}
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    <div className="flex items-end gap-2 h-48">
                      {stats.monthly_stats.map((month, index) => (
                        <div key={month.month_key} className="flex-1 flex flex-col items-center gap-1">
                          <div className="flex items-end gap-1 h-40 w-full justify-center">
                            {/* Added Bar */}
                            <div 
                              className="w-5 bg-primary rounded-t transition-all duration-300 hover:brightness-110"
                              style={{ 
                                height: `${(month.added / maxMonthlyValue) * 100}%`,
                                minHeight: month.added > 0 ? '8px' : '0'
                              }}
                              title={`Added: ${month.added}`}
                            />
                            {/* Approved Bar */}
                            <div 
                              className="w-5 bg-green-500 rounded-t transition-all duration-300 hover:brightness-110"
                              style={{ 
                                height: `${(month.approved / maxMonthlyValue) * 100}%`,
                                minHeight: month.approved > 0 ? '8px' : '0'
                              }}
                              title={`Approved: ${month.approved}`}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {month.month}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Monthly Table */}
                <div className="mt-6 border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-muted-foreground">Month</th>
                        <th className="px-4 py-2 text-right font-medium text-muted-foreground">Added</th>
                        <th className="px-4 py-2 text-right font-medium text-muted-foreground">Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...stats.monthly_stats].reverse().map((month, index) => (
                        <tr key={month.month_key} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-medium">{month.month}</td>
                          <td className="px-4 py-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              {month.added}
                              {month.added > 0 && <TrendingUp className="w-3 h-3 text-primary" />}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              {month.approved}
                              {month.approved > 0 && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No monthly data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canEdit() && (
                <Link to="/emission-factors/new" className="block">
                  <Button variant="outline" className="w-full justify-between" data-testid="quick-action-new">
                    Create Emission Factor
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
              <Link to="/emission-factors" className="block">
                <Button variant="outline" className="w-full justify-between" data-testid="quick-action-view-all">
                  View All Factors
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              {canApprove() && (
                <Link to="/pending-approvals" className="block">
                  <Button variant="outline" className="w-full justify-between" data-testid="quick-action-pending">
                    Review Pending ({stats.pending})
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
              <Link to="/audit-logs" className="block">
                <Button variant="outline" className="w-full justify-between" data-testid="quick-action-audit">
                  View Audit Logs
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-gray-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : stats.recent_activity?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_activity.map((activity, index) => (
                    <div 
                      key={activity.id || index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      data-testid={`activity-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.action === 'approve' ? 'bg-green-500' :
                          activity.action === 'reject' ? 'bg-red-500' :
                          activity.action === 'create' ? 'bg-blue-500' :
                          'bg-amber-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium">
                            {activity.user_name} {getActionLabel(activity.action).toLowerCase()} an emission factor
                          </p>
                          <p className="text-xs text-muted-foreground">{formatDate(activity.timestamp)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No recent activity</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
